import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Users, PhoneCall, CheckCircle2, RefreshCw, Trash2, MessageCircle, Send, Loader2 } from "lucide-react";
import { waLink } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["novo", "em contato", "fechado"];

const statusStyle = {
  "novo": "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  "em contato": "bg-amber-500/15 text-amber-400 border-amber-500/30",
  "fechado": "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

export const LeadsTab = ({ onUnauthorized }) => {
  const [leads, setLeads] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySaving, setReplySaving] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const responderMl = async (id) => {
    if (!replyText.trim()) return toast.error("Escreva a resposta antes de enviar.");
    setReplySaving(true);
    try {
      await axios.post(`${API}/leads/${id}/ml_responder`, { texto: replyText }, { headers });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, ml_status: "ANSWERED" } : l)));
      setReplyFor(null);
      setReplyText("");
      toast.success("Resposta enviada ao Mercado Livre!");
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else if (e.response?.status === 409) toast.error("Conta do Mercado Livre não conectada (Conta > Integrações).");
      else {
        const d = e.response?.data?.detail;
        toast.error(d?.ml_body?.message ? `Mercado Livre: ${d.ml_body.message}` : (typeof d === "string" ? d : "Erro ao responder."));
      }
    } finally { setReplySaving(false); }
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leads`, { headers });
      setLeads(res.data);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else { setLeads([]); toast.error("Erro ao carregar leads."); }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status }, { headers });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status atualizado.");
    } catch { toast.error("Erro ao atualizar status."); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { headers });
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead removido.");
    } catch { toast.error("Erro ao remover lead."); }
  };

  const counts = {
    total: leads?.length || 0,
    novos: leads?.filter((l) => (l.status || "novo") === "novo").length || 0,
    fechados: leads?.filter((l) => l.status === "fechado").length || 0,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-white">Leads recebidos</h1>
        <button
          onClick={load} data-testid="admin-refresh-button"
          className="inline-flex items-center gap-2 glass hover:border-emerald-500/50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="admin-stats">
        {[
          { icon: Users, label: "Leads totais", value: counts.total },
          { icon: PhoneCall, label: "Novos (aguardando)", value: counts.novos },
          { icon: CheckCircle2, label: "Fechados", value: counts.fechados },
        ].map((s) => (
          <div key={s.label} className="glass rounded-2xl p-6 flex items-center gap-4">
            <span className="w-12 h-12 rounded-2xl bg-emerald-500/15 flex items-center justify-center">
              <s.icon className="w-6 h-6 text-emerald-400" />
            </span>
            <div>
              <p className="font-display text-3xl font-bold text-white">{s.value}</p>
              <p className="text-xs text-slate-400">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {leads === null ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 rounded-2xl bg-[#131620] animate-pulse" />)}</div>
      ) : leads.length === 0 ? (
        <p className="text-slate-400 text-sm glass rounded-2xl p-8" data-testid="admin-empty">Nenhum lead por enquanto.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-leads-list">
          {leads.map((l) => (
            <motion.div
              key={l.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center gap-4"
              data-testid={`admin-lead-${l.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white">{l.nome}</p>
                <p className="text-sm text-slate-400 truncate">
                  {l.whatsapp} {l.email ? `· ${l.email}` : ""} · {l.interesse}
                </p>
                {l.mensagem && <p className="text-xs text-slate-500 mt-1 truncate">"{l.mensagem}"</p>}
                <p className="text-[11px] text-slate-600 mt-1 font-mono2">
                  <span className={l.origem === "mercadolivre" ? "text-yellow-400 font-semibold" : ""} data-testid={`lead-origem-${l.id}`}>
                    {{ formulario: "Formulário do site", simulador: "Simulador", mercadolivre: "Mercado Livre" }[l.origem] || l.origem}
                  </span>
                  {" · "}{l.created_at ? new Date(l.created_at).toLocaleString("pt-BR") : ""}
                </p>
                {l.origem === "mercadolivre" && l.ml_question_id && (
                  l.ml_status === "ANSWERED" ? (
                    <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1.5" data-testid={`lead-ml-respondida-${l.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Respondida no Mercado Livre
                    </p>
                  ) : replyFor === l.id ? (
                    <div className="mt-3 flex gap-2" data-testid={`lead-ml-reply-form-${l.id}`}>
                      <input
                        autoFocus value={replyText} onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), responderMl(l.id))}
                        placeholder="Responder ao comprador no Mercado Livre..." maxLength={2000}
                        className="flex-1 bg-[#0d0f17] border border-slate-800 focus:border-yellow-400 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors"
                        data-testid={`lead-ml-reply-input-${l.id}`}
                      />
                      <button
                        onClick={() => responderMl(l.id)} disabled={replySaving}
                        data-testid={`lead-ml-reply-send-${l.id}`}
                        className="shrink-0 inline-flex items-center gap-1.5 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-yellow-950 text-sm font-bold px-4 rounded-xl transition-colors"
                      >
                        {replySaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Enviar
                      </button>
                      <button onClick={() => { setReplyFor(null); setReplyText(""); }} className="text-slate-400 hover:text-white text-sm px-2">Cancelar</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setReplyFor(l.id); setReplyText(""); }}
                      data-testid={`lead-ml-reply-open-${l.id}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-yellow-400 hover:text-yellow-300 transition-colors"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> Responder no Mercado Livre
                    </button>
                  )
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={waLink(`Olá ${l.nome}! Aqui é da Seed Motors, recebemos seu contato pelo site.`)}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`admin-lead-wa-${l.id}`}
                  className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                  aria-label="Chamar no WhatsApp"
                >
                  <MessageCircle className="w-4 h-4" />
                </a>
                <select
                  value={l.status || "novo"}
                  onChange={(e) => setStatus(l.id, e.target.value)}
                  data-testid={`admin-lead-status-${l.id}`}
                  className={`text-xs font-semibold rounded-full px-3 py-2 border bg-transparent outline-none cursor-pointer ${statusStyle[l.status || "novo"]}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-[#131620] text-white">{s}</option>)}
                </select>
                <button
                  onClick={() => remove(l.id)}
                  data-testid={`admin-lead-delete-${l.id}`}
                  className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/25 transition-colors"
                  aria-label="Remover lead"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};
